/* eslint-disable max-lines */
import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, DestroyRef, inject, input, Input, OnDestroy, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { DefaultLocale } from '@app/+authenticated/shared/locale/locale.helper';
import { MailtoPipe } from '@app/pipes/mailto.pipe';
import { SharedPipeModule } from '@app/pipes/shared-pipe.module';
import { SubscriptionCurrencyPipe } from '@app/pipes/subscription-currency.pipe';
import { getAccountState } from '@app/reducers/account/account-service.selectors';
import { getDisplayPrice, PlanTypeWeight } from '@app/reducers/account/account-subscription.helper';
import { AccountState } from '@app/reducers/account/account.model';
import { accountHasChatAddon, accountHasHrProAccess } from '@app/reducers/account/account.selector';
import { trackByIdFn } from '@app/shared/trackby';
import { select, Store } from '@ngrx/store';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { AppState } from '@reducers/interfaces';
import { featureFlag, FeatureService } from '@sb/shared/feature';
import { TooltipDirective } from '@sb/tooltip';
import { BadgeComponent } from '@sb/ui/badge';
import { NoticeComponent } from '@sb/ui/notice';
import { SwitchComponent } from '@sb/ui/switch';
import { InvoicePeriodType, PlanType, QuotaType, SubscriptionModel } from '@shiftbase-com/ts-shared/models';
import { Subscription } from 'rxjs';
import { distinctUntilChanged, filter, switchMap, take } from 'rxjs/operators';

import { AddOn, SubscriptionService } from '../../../../+reports/shared/subscriptions/subscription.service';
import { AddonCardComponent } from './addon-card/addon-card.component';
import { getAllFeaturesUrl } from './choose-plan-card.helper';
import { newPlanFeatures, oldPlanFeatures, planFeatures } from './choose-plan-card.model';
import { PlanCardComponent } from './plan-card/plan-card.component';

@Component({
  selector: 'choose-plan-card',
  templateUrl: './choose-plan-card.component.html',
  imports: [
    CommonModule,
    TranslatePipe,
    SharedPipeModule,
    TooltipDirective,
    PlanCardComponent,
    BadgeComponent,
    RouterModule,
    MailtoPipe,
    NoticeComponent,
    SwitchComponent,
    ReactiveFormsModule,
    SubscriptionCurrencyPipe,
    AddonCardComponent,
  ],
})
export class ChoosePlanCardComponent implements OnInit, OnDestroy {
  private subscriptionService = inject(SubscriptionService);
  private store = inject<Store<AppState>>(Store);
  private translate = inject(TranslateService);
  private cd = inject(ChangeDetectorRef);
  private featureService = inject(FeatureService);
  private destroyRef = inject(DestroyRef);

  @Input()
  yearlyBillingToggle: FormControl<boolean>;

  @Input()
  trialExpired = false;

  @Input()
  forceUpgrade = false;

  // Overrides which plan the "Current plan" marker highlights. The trial-expired flow passes the plan
  // the account was on before it was converted to Free, so that shows as current instead of the live
  // Free plan. Falls back to the live subscription when unset.
  currentPlanId = input<string>();

  subscriptions: SubscriptionModel[] = [];
  availableSubscriptions: SubscriptionModel[] = [];

  trackBy = trackByIdFn;
  hasPaymentMethod: boolean;
  currentSubscription: SubscriptionModel;
  selectedSubscription: SubscriptionModel;
  isLoading: boolean;

  isFetchingSubscriptions = true;

  quotaType = QuotaType;
  planType = PlanType;
  planTypeWeight = PlanTypeWeight;
  planFeatures = planFeatures;
  countryCode: string;
  hasEnterprisePlan: boolean;
  hasIntegrationPlus: boolean;
  hasCompliancePlus: boolean;
  hasHrPlus: boolean;
  hasChat: boolean;
  hasTrial: boolean;
  Features = featureFlag;

  private integrationPlusMonthPrice: number;
  private integrationPlusYearPrice: number;

  private compliancePlusMonthPrice: number;
  private compliancePlusYearPrice: number;

  private hrPlusMonthPrice: number;
  private hrPlusYearPrice: number;

  private chatMonthPrice: number;
  private chatYearPrice: number;

  private dataSubs = new Subscription();
  inTrialPeriod: boolean;

  integrationPlus = new FormControl(false);
  compliancePlus = new FormControl(false);
  hrPlus = new FormControl(false);
  chat = new FormControl(false);

  featureCompliancePlusAddonEnabled: boolean;
  featureHrProAddonEnabled: boolean;
  featureChatAddonEnabled: boolean;

  invoicePeriod: InvoicePeriodType;
  invoicePeriodType = InvoicePeriodType;

  switchToFreeLabel: string;
  switchToBasicLabel: string;
  switchToPremiumLabel: string;
  displayPrice: string;

  featuresUrl: string;

  trialGranted: boolean;

  integrationPlusPrice: number;
  compliancePlusPrice: number;
  hrPlusPrice: number;
  chatPrice: number;

  constructor() {
    this.featuresUrl = this.getFeaturesUrl();
    this.planFeatures = this.getPlanFeatures();
  }

  ngOnInit(): void {
    this.setAddonFeatureFlags();

    this.setAccountSubscriptionData();

    this.dataSubs.add(
      this.subscriptionService.getSubscriptions().subscribe((subscriptions) => {
        const validatedSubscriptions = this.validateSubscriptionQuota(subscriptions);
        this.isFetchingSubscriptions = false;
        this.checkCustomSubscription();

        this.subscriptions = this.orderSubscriptions(validatedSubscriptions);

        const hasAnyEntitledSubscriptions = this.subscriptions.some((sub) => sub.is_entitled);
        if (hasAnyEntitledSubscriptions) {
          // Feature flag ON: Filter to show entitled subscriptions + Free plan in the main grid
          this.availableSubscriptions = this.subscriptions.filter(
            (sub) => sub.is_entitled || sub.plan.type === PlanType.FREE,
          );
        } else {
          // Feature flag OFF: Show all subscriptions (legacy mode)
          this.availableSubscriptions = this.subscriptions;
        }

        if (this.forceUpgrade) {
          this.availableSubscriptions = this.filterSubscriptionDowngrades(this.availableSubscriptions);
          this.selectHighestSubscriptionPlan();
        }

        this.cd.detectChanges();
      }),
    );

    this.dataSubs.add(
      this.subscriptionService.getSelectedSubscription().subscribe({
        next: (subscription) => {
          if (!subscription?.subscription) {
            return;
          }

          this.selectedSubscription = subscription.subscription;

          this.checkCustomSubscription();
          this.displayPrice = subscription.subscription.display_price;

          if (this.selectedSubscription.plan.type === this.planType.FREE) {
            this.integrationPlus.patchValue(false, { emitEvent: false });
            this.hasIntegrationPlus = false;
            this.integrationPlus.disable({ emitEvent: false });

            this.compliancePlus.patchValue(false, { emitEvent: false });
            this.hasCompliancePlus = false;
            this.compliancePlus.disable({ emitEvent: false });

            this.hrPlus.patchValue(false, { emitEvent: false });
            this.hasHrPlus = false;
            this.hrPlus.disable({ emitEvent: false });

            this.chat.patchValue(false, { emitEvent: false });
            this.hasChat = false;
            this.chat.disable({ emitEvent: false });
          }

          // when enterprise plan is selected, integration plus and compliance plus is enabled and checked by default and users cannot change this without sales
          if (this.selectedSubscription.plan.type === this.planType.ENTERPRISE) {
            this.integrationPlus.patchValue(true, { emitEvent: false });
            this.hasIntegrationPlus = true;
            this.integrationPlus.disable({ emitEvent: false });

            this.compliancePlus.patchValue(true, { emitEvent: false });
            this.hasCompliancePlus = true;
            this.compliancePlus.disable({ emitEvent: false });
          }

          if (this.selectedSubscription.plan.type === PlanType.FREE) {
            this.yearlyBillingToggle.disable();
            this.yearlyBillingToggle.setValue(false);
          } else {
            this.yearlyBillingToggle.enable();
          }

          this.cd.detectChanges();
        },
      }),
    );

    this.dataSubs.add(
      this.yearlyBillingToggle.valueChanges.pipe(distinctUntilChanged()).subscribe((yearlyBillingValue) => {
        this.invoicePeriod = yearlyBillingValue ? InvoicePeriodType.YEAR : InvoicePeriodType.MONTH;
        this.displayPrice = yearlyBillingValue
          ? this.selectedSubscription.display_year_price
          : this.selectedSubscription.display_price;

        this.updateAddonPrices(yearlyBillingValue);
        this.setSelectedSubscription();
        this.cd.detectChanges();
      }),
    );

    this.subscribeToAddonValueChanges();
  }

  private updateAddonPrices(yearly: boolean) {
    this.integrationPlusPrice = yearly ? this.integrationPlusYearPrice : this.integrationPlusMonthPrice;
    this.compliancePlusPrice = yearly ? this.compliancePlusYearPrice : this.compliancePlusMonthPrice;
    this.hrPlusPrice = yearly ? this.hrPlusYearPrice : this.hrPlusMonthPrice;
    this.chatPrice = yearly ? this.chatYearPrice : this.chatMonthPrice;
  }

  private setAddonFeatureFlags() {
    this.featureCompliancePlusAddonEnabled = this.featureService.isEnabled(featureFlag.TMP_COMPLIANCE_PLUS_ADDON);
    this.featureHrProAddonEnabled = this.featureService.isEnabled(featureFlag.TMP_HR_PRO_ADDON);
    this.featureChatAddonEnabled = this.featureService.isEnabled(featureFlag.TMP_CHAT_ADDON);
  }

  private subscribeToAddonValueChanges() {
    this.integrationPlus.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((integrationPlusValue) => {
      this.hasIntegrationPlus = integrationPlusValue;
      this.setSelectedSubscription();
    });
    // todo: remove if statement once feature flag is removed tmp_compliance_plus_addon
    if (this.featureCompliancePlusAddonEnabled) {
      this.compliancePlus.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((compliancePlusValue) => {
        this.hasCompliancePlus = compliancePlusValue;
        this.setSelectedSubscription();
      });
    }

    if (this.featureHrProAddonEnabled) {
      this.hrPlus.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((hrPlusValue) => {
        this.hasHrPlus = hrPlusValue;
        this.setSelectedSubscription();
      });
    }

    if (this.featureChatAddonEnabled) {
      this.chat.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((chatValue) => {
        this.hasChat = chatValue;
        this.setSelectedSubscription();
      });
    }
  }

  private setSelectedSubscription() {
    this.subscriptionService.setSelectedSubscription({
      subscription: this.selectedSubscription,
      integrationPlus: this.hasIntegrationPlus,
      invoicePeriod: this.invoicePeriod,
      compliance: this.hasCompliancePlus,
      hrPlus: this.hasHrPlus,
      chat: this.hasChat,
    });
  }

  getFeatureTranslationParams(subscription: SubscriptionModel): Record<string, unknown> {
    const seatsQuota = subscription.plan.quotas?.[QuotaType.SEATS];
    return { seatsLimit: seatsQuota?.limit ?? '' };
  }

  getAdditionalEmployeeCost(
    subscription: SubscriptionModel,
  ): { cost: string; costText: string; billingPeriod: string } | undefined {
    const featureConfig = this.planFeatures[subscription.plan.weight];

    // Only show when using new pricing structure
    if (!featureConfig?.useNewPricingStructure) {
      return undefined;
    }

    if (subscription.plan.type === PlanType.FREE) {
      return undefined;
    }

    if (!subscription.seat_overage_price && !subscription.seat_overage_year_price) {
      return undefined;
    }

    const isYearly = this.yearlyBillingToggle.value;

    const price = isYearly ? subscription.display_seat_overage_year_price : subscription.display_seat_overage_price;

    if (!price) {
      console.warn(
        'Missing overage price for current billing period:',
        subscription.plan.type,
        isYearly ? 'yearly' : 'monthly',
      );
      return undefined;
    }

    const costText = this.translate.instant('Add unlimited employees for {{price}} per employee.', {
      price,
    });

    const billingPeriod = isYearly
      ? this.translate.instant('Per month, billed yearly')
      : this.translate.instant('Per month');

    return {
      cost: price,
      costText,
      billingPeriod,
    };
  }

  determineDisabledStateForPlanCards(subscription: SubscriptionModel) {
    if (!(this.inTrialPeriod || this.trialExpired)) {
      return false;
    }

    const differentSubscription = this.currentSubscription?.id !== subscription.id;
    const quotaReached = subscription.quotaReached?.length > 0;

    return (
      (differentSubscription && quotaReached) ||
      (differentSubscription &&
        this.currentSubscription.invoice_period === this.invoicePeriodType.YEAR &&
        this.currentSubscription.plan.weight > subscription.plan.weight &&
        !this.hasTrial)
    );
  }

  setAccountSubscriptionData() {
    this.dataSubs.add(
      this.store
        .pipe(
          select(getAccountState),
          filter((accountState) => !!accountState),
          switchMap((accountState: AccountState) => {
            this.currentSubscription = accountState.subscription;
            this.hasIntegrationPlus = accountState.account.integration_plus;
            this.hasCompliancePlus = accountState.account.schedule_compliance_check;
            this.hasHrPlus = accountHasHrProAccess(accountState.account);
            this.hasChat = accountHasChatAddon(accountState.account);
            this.invoicePeriod = accountState.subscription.invoice_period;
            this.hasTrial = accountState.account.hasTrial;

            this.trialGranted = accountState.subscription.trial_granted;

            if (!this.subscriptionService.getHasSetValue()) {
              this.subscriptionService.setSelectedSubscription({
                subscription: this.currentSubscription,
                integrationPlus: this.hasIntegrationPlus,
                invoicePeriod: this.invoicePeriod,
                compliance: this.hasCompliancePlus,
                hrPlus: this.hasHrPlus,
                chat: this.hasChat,
              });
            } else {
              this.hasIntegrationPlus = this.subscriptionService.getIntegrationPlus();
              this.hasCompliancePlus = this.subscriptionService.getCompliancePlus();
              this.hasHrPlus = this.subscriptionService.getHrPlus();
              this.hasChat = this.subscriptionService.getChat();
              this.invoicePeriod = this.subscriptionService.getInvoicePeriod();
            }

            this.hasPaymentMethod = accountState.account.continue_subscription;
            this.inTrialPeriod = accountState.account.hasTrial;
            this.countryCode = accountState.account.country;
            this.integrationPlus.setValue(this.hasIntegrationPlus);
            this.compliancePlus.setValue(this.hasCompliancePlus);
            this.hrPlus.setValue(this.hasHrPlus);
            this.chat.setValue(this.hasChat);
            this.yearlyBillingToggle.setValue(this.invoicePeriod === InvoicePeriodType.YEAR);

            this.checkCustomSubscription();

            return this.subscriptionService.getAddonPrice().pipe(take(1));
          }),
        )
        .subscribe((addOn: AddOn) => {
          this.integrationPlusMonthPrice = addOn.integrationPlusPrice;
          this.integrationPlusYearPrice = addOn.integrationPlusYearPrice;
          this.integrationPlusPrice = this.yearlyBillingToggle.value
            ? this.integrationPlusYearPrice
            : this.integrationPlusMonthPrice;

          this.compliancePlusMonthPrice = addOn.compliancePrice;
          this.compliancePlusYearPrice = addOn.complianceYearPrice;
          this.compliancePlusPrice = this.yearlyBillingToggle.value
            ? this.compliancePlusYearPrice
            : this.compliancePlusMonthPrice;

          this.hrPlusMonthPrice = addOn.hrPlusPrice;
          this.hrPlusYearPrice = addOn.hrPlusYearPrice;
          this.hrPlusPrice = this.yearlyBillingToggle.value ? this.hrPlusYearPrice : this.hrPlusMonthPrice;

          this.chatMonthPrice = addOn.chatPrice;
          this.chatYearPrice = addOn.chatYearPrice;
          this.chatPrice = this.yearlyBillingToggle.value ? this.chatYearPrice : this.chatMonthPrice;

          this.cd.detectChanges();
        }),
    );
  }

  getDisplayPrice(subscription: SubscriptionModel) {
    const invoicePeriod = this.yearlyBillingToggle.value ? InvoicePeriodType.YEAR : InvoicePeriodType.MONTH;
    return getDisplayPrice(this.currentSubscription, subscription, invoicePeriod);
  }

  selectSubscription(subscription: SubscriptionModel) {
    if (subscription.plan.type !== PlanType.FREE && !this.hasIntegrationPlus && !this.yearlyBillingToggle.value) {
      this.integrationPlus.enable({ emitEvent: false });
    }
    if (subscription.plan.type !== PlanType.FREE && !this.hasCompliancePlus && !this.yearlyBillingToggle.value) {
      this.compliancePlus.enable({ emitEvent: false });
    }
    if (
      this.featureHrProAddonEnabled &&
      subscription.plan.type !== PlanType.FREE &&
      !this.hasHrPlus &&
      !this.yearlyBillingToggle.value
    ) {
      this.hrPlus.enable({ emitEvent: false });
    }
    if (
      this.featureChatAddonEnabled &&
      subscription.plan.type !== PlanType.FREE &&
      !this.hasChat &&
      !this.yearlyBillingToggle.value
    ) {
      this.chat.enable({ emitEvent: false });
    }
    this.subscriptionService.setSelectedSubscription({
      subscription: subscription,
      // If subscription is free, integrationPlus toggle is disabled
      // and unchecked but the value is updated at the same time this method is called
      // so the value is not updated yet at this point
      integrationPlus: subscription.plan.type === PlanType.FREE ? false : this.hasIntegrationPlus,
      compliance: subscription.plan.type === PlanType.FREE ? false : this.hasCompliancePlus,
      hrPlus: subscription.plan.type === PlanType.FREE ? false : this.hasHrPlus,
      chat: subscription.plan.type === PlanType.FREE ? false : this.hasChat,
      invoicePeriod:
        this.yearlyBillingToggle.value && subscription.plan.type !== PlanType.FREE
          ? InvoicePeriodType.YEAR
          : InvoicePeriodType.MONTH,
    });
  }

  ngOnDestroy(): void {
    this.dataSubs.unsubscribe();
  }

  /**
   * Shows the box when:
   * - Current subscription is NOT entitled (is_entitled = false)
   * - AND current subscription is NOT the Free plan (Free is always current, never legacy)
   * - AND there are entitled subscriptions available (meaning new plans exist and feature flag is ON)
   * - AND user is not on Enterprise (Enterprise has its own special display)
   *
   * This indicates the user is on an old plan that's no longer offered.
   */
  get shouldShowLegacyPlanBox(): boolean {
    if (!this.currentSubscription || this.hasEnterprisePlan) {
      return false;
    }

    // Free plans are never legacy
    if (this.currentSubscription.plan.type === PlanType.FREE) {
      return false;
    }

    // Safety check: if is_entitled is undefined, check if current sub is in available subscriptions
    // This handles cases where the first API call doesn't include is_entitled field yet
    if (this.currentSubscription.is_entitled === undefined) {
      const currentIsInAvailableList = this.availableSubscriptions.some(
        (sub) => sub.id === this.currentSubscription.id,
      );
      // If current sub is in available list, it's entitled, so don't show legacy subscription window
      return !currentIsInAvailableList;
    }

    const currentIsLegacy = !this.currentSubscription.is_entitled;
    const entitledPlansExist = this.availableSubscriptions.some((sub) => sub.is_entitled === true);

    return currentIsLegacy && entitledPlansExist;
  }

  /**
   * SAFEGUARD: Determines if we should hide all plan options to protect users on entitled plans
   * when the feature flag is turned OFF.
   *
   * This prevents users who signed up for new entitled plans from accidentally
   * downgrading to legacy plans if the feature flag gets disabled.
   *
   * @returns true if we should hide plan options and show a notice instead
   */
  get shouldHidePlansForEntitledSubscription(): boolean {
    if (!this.currentSubscription) {
      return false;
    }

    const isFeatureFlagOff = !this.featureService.isEnabled(this.Features.TMP_ENTITLED_SUBSCRIPTIONS);

    const isOnEntitledPlan = this.currentSubscription.is_entitled === true;

    // If feature flag is OFF and user is on an entitled plan, hide all options
    return isFeatureFlagOff && isOnEntitledPlan;
  }

  private orderSubscriptions(subscriptions: SubscriptionModel[]): SubscriptionModel[] {
    return subscriptions.sort((a, b) => this.planTypeWeight[a.plan.type] - this.planTypeWeight[b.plan.type]);
  }

  private getFeaturesUrl() {
    const locale = this.translate.getCurrentLang() ?? DefaultLocale;

    return (
      getAllFeaturesUrl(locale) ??
      getAllFeaturesUrl(locale.slice(0, 2)) ??
      'https://www.shiftbase.com/pricing#price-table'
    );
  }

  private getPlanFeatures() {
    const isEntitledSubscriptionsEnabled = this.featureService.isEnabled(this.Features.TMP_ENTITLED_SUBSCRIPTIONS);

    if (isEntitledSubscriptionsEnabled) {
      return newPlanFeatures;
    }

    return oldPlanFeatures;
  }

  private validateSubscriptionQuota(subscriptions: SubscriptionModel[]): SubscriptionModel[] {
    if (!subscriptions) {
      return [];
    }

    return subscriptions.map((subscription) => {
      const quotas = Object.values(subscription.plan.quotas);
      const quotaReached = quotas.filter((quota) => quota.usage > quota.limit);

      return {
        ...subscription,
        quotaReached,
      };
    });
  }

  private checkCustomSubscription() {
    if (!this.currentSubscription || !this.subscriptions) {
      return;
    }

    this.hasEnterprisePlan = this.currentSubscription.plan.weight === this.planTypeWeight[PlanType.ENTERPRISE];
  }

  private selectHighestSubscriptionPlan() {
    const highestPlan = this.availableSubscriptions.reduce((prev, current) =>
      prev.plan.weight > current.plan.weight ? prev : current,
    );
    this.selectSubscription(highestPlan);
  }

  private filterSubscriptionDowngrades(subscriptions: SubscriptionModel[]): SubscriptionModel[] {
    return subscriptions.filter((subscription) => subscription.plan.weight > this.currentSubscription.plan.weight);
  }
}
